import { useParams, Link } from 'react-router-dom';
import { useEffect } from 'react';
import Breadcrumb from '@/components/base/Breadcrumb';
import { articles } from '@/mocks/articles';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [id]);

  const article = articles.find((a) => a.id === id);

  if (!article) {
    return (
      <main className="w-full px-4 md:px-6 py-20">
        <div className="max-w-3xl mx-auto text-center">
          <div className="w-20 h-20 flex items-center justify-center mx-auto mb-6">
            <i className="ri-error-warning-line text-5xl text-foreground-300"></i>
          </div>
          <h1 className="font-heading text-2xl font-bold text-foreground-900 mb-3">
            文章未找到
          </h1>
          <p className="text-foreground-500 mb-6">
            您访问的文章不存在或已被移除
          </p>
          <Link
            to="/"
            className="inline-flex items-center px-6 py-2.5 bg-primary-500 text-white font-medium rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            返回首页
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main>
      <Breadcrumb
        items={[
          { label: '影视资讯', to: '/' },
          { label: article.category },
          { label: article.title },
        ]}
      />

      <article className="w-full px-4 md:px-6 py-8">
        <div className="max-w-3xl mx-auto">
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-4 flex-wrap">
              <span className="px-3 py-1 text-xs font-medium bg-primary-100 text-primary-700 rounded-full whitespace-nowrap">
                {article.category}
              </span>
              <span className="text-sm text-foreground-500">{article.date}</span>
              <span className="text-sm text-foreground-500">· {article.author}</span>
            </div>

            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 leading-tight mb-4">
              {article.title}
            </h1>

            <p className="text-foreground-600 leading-relaxed text-base">
              {article.summary}
            </p>

            <div className="flex items-center gap-2 mt-4 flex-wrap">
              {article.tags.map((tag: string) => (
                <span
                  key={tag}
                  className="px-2 py-0.5 text-xs bg-background-100 text-foreground-500 rounded whitespace-nowrap"
                >
                  #{tag}
                </span>
              ))}
            </div>
          </div>

          <div className="aspect-[16/9] rounded-lg overflow-hidden mb-8">
            <img
              src={article.imageUrl}
              alt={article.title}
              title={article.title}
              className="w-full h-full object-cover"
            />
          </div>

          <div
            className="prose prose-lg max-w-none text-foreground-700 leading-relaxed"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          <div className="mt-12 pt-8 border-t border-background-200">
            <h3 className="font-heading text-lg font-bold text-foreground-900 mb-4">
              相关推荐
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {articles
                .filter((a: typeof article) => a.id !== article.id)
                .slice(0, 2)
                .map((related: typeof article) => (
                  <Link
                    key={related.id}
                    to={`/article/${related.id}`}
                    className="flex gap-3 p-3 rounded-lg border border-background-200 hover:border-primary-300 transition-all cursor-pointer"
                  >
                    <div className="w-24 h-16 rounded overflow-hidden flex-shrink-0">
                      <img
                        src={related.imageUrl}
                        alt={related.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-heading font-bold text-foreground-800 text-sm line-clamp-2">
                        {related.title}
                      </h4>
                      <p className="text-xs text-foreground-500 mt-1">{related.date}</p>
                    </div>
                  </Link>
                ))}
            </div>
          </div>
        </div>
      </article>
    </main>
  );
}